/** Fog of war, per player, inside the simulation.
 *
 * Fog is a rule, not a graphics overlay: it decides what a `PlayerObservation`
 * may contain (I9), so it has to live in state and hash with it. A renderer that
 * owned the fog would leak the whole map to an AI and to a determined player.
 *
 * Reference "# 10. Fog of War and Scouting".
 */

import type { AdventureMapState, FogState, FogVisibility } from "../state.js";
import { cellIndex, inBounds } from "../state.js";
import { entityKeys } from "../util/iter.js";

/** 0 unseen, 1 remembered (explored, currently out of sight), 2 visible now. */
export const FOG_UNSEEN: FogVisibility = 0;
export const FOG_REMEMBERED: FogVisibility = 1;
export const FOG_VISIBLE: FogVisibility = 2;

/** A hero with no Scouting sees this far. Towns and most watch objects match it. */
export const BASE_SCOUTING_RADIUS = 5;

/**
 * The expansion Scouting: +1 / +3 / +5 sight radius at basic / advanced / expert.
 * Index 0 is "no Scouting". The classic's +1/+2/+3 is the other reading; the expansion is the
 * one the reference states, so it is the one implemented.
 */
export const SCOUTING_RADIUS_BONUS: readonly number[] = Object.freeze([0, 1, 3, 5]);

export const scoutingRadius = (level: 0 | 1 | 2 | 3, base = BASE_SCOUTING_RADIUS): number =>
  base + SCOUTING_RADIUS_BONUS[level]!;

export function createFog(map: AdventureMapState): FogState {
  return { seen: new Uint8Array(map.width * map.height * map.levels), memory: {} };
}

/** Everything hidden again; used by scenario scripts and by tests. */
export function clearFog(fog: FogState): void {
  fog.seen.fill(FOG_UNSEEN);
}

/** The whole map explored, e.g. for a scenario that starts uncovered. */
export function revealAll(fog: FogState, level: FogVisibility = FOG_REMEMBERED): void {
  fog.seen.fill(level);
}

/**
 * True when `(dx, dy)` is inside a scouting circle of `radius`.
 *
 * The classic reveals a disc, not a square: the cell is lit when its squared
 * Euclidean distance from the hero is within radius squared. Integer-only, so
 * it is exact, and symmetric in both axes by construction - which is what makes
 * "A sees B implies B sees A" hold for equal radii.
 */
export const inScoutingDisc = (dx: number, dy: number, radius: number): boolean =>
  dx * dx + dy * dy <= radius * radius;

/**
 * Light the disc of `radius` around `(x, y, z)` and return the flat indices of
 * cells that were not already visible, in ascending order, for `FogRevealed`.
 *
 * Mutates `fog.seen` in place: it is a dense derived plane like `map.moveCost`,
 * and copying a 20k-cell array on every step of every hero is the kind of thing
 * that turns a turn into a stutter. Sight never crosses levels - the
 * underground is a separate plane and is revealed by walking it.
 */
export function revealAround(
  fog: FogState, x: number, y: number, z: number, radius: number, map: AdventureMapState,
): number[] {
  const revealed: number[] = [];
  if (radius < 0) return revealed;
  if (z < 0 || z >= map.levels) return revealed;

  const r = Math.trunc(radius);
  const y0 = Math.max(0, y - r), y1 = Math.min(map.height - 1, y + r);
  const x0 = Math.max(0, x - r), x1 = Math.min(map.width - 1, x + r);

  for (let cy = y0; cy <= y1; cy++) {
    const dy = cy - y;
    for (let cx = x0; cx <= x1; cx++) {
      const dx = cx - x;
      if (!inScoutingDisc(dx, dy, r)) continue;
      const i = cellIndex(map, cx, cy, z);
      if (fog.seen[i] !== FOG_VISIBLE) revealed.push(i);
      fog.seen[i] = FOG_VISIBLE;
    }
  }
  return revealed;
}

/**
 * Demote everything currently visible to remembered. Run this once at the start
 * of a player's visibility recomputation, then `revealAround` every asset that
 * player owns; explored ground is never un-explored, only un-watched.
 */
export function demoteVisible(fog: FogState): void {
  const s = fog.seen;
  for (let i = 0; i < s.length; i++) if (s[i] === FOG_VISIBLE) s[i] = FOG_REMEMBERED;
}

// ------------------------------------------------------------------ queries

export function visibilityAt(
  fog: FogState, map: AdventureMapState, x: number, y: number, z: number,
): FogVisibility {
  if (!inBounds(map, x, y, z)) return FOG_UNSEEN;
  return (fog.seen[cellIndex(map, x, y, z)] ?? FOG_UNSEEN) as FogVisibility;
}

export const visibilityAtIndex = (fog: FogState, i: number): FogVisibility =>
  (fog.seen[i] ?? FOG_UNSEEN) as FogVisibility;

/** Currently watched: live object state may be shown. */
export const isVisible = (fog: FogState, i: number): boolean => fog.seen[i] === FOG_VISIBLE;

/** Explored at some point: terrain may be shown, object state only from memory. */
export const isExplored = (fog: FogState, i: number): boolean =>
  (fog.seen[i] ?? FOG_UNSEEN) !== FOG_UNSEEN;

export function countExplored(fog: FogState): number {
  let c = 0;
  for (let i = 0; i < fog.seen.length; i++) if (fog.seen[i] !== FOG_UNSEEN) c++;
  return c;
}

// ------------------------------------------------------------------- memory

export type FogSnapshot = Readonly<Record<string, number | string | null>>;

/**
 * Record what a player last saw of an entity.
 *
 * Deliberately stale: when an enemy hero walks out of sight the player keeps
 * showing the army he saw, not the army that exists. Returns a new `FogState`
 * (the seen plane is shared, memory is a plain record and is replaced) and the
 * keys are written back in sorted order so two states that recorded the same
 * facts in a different order serialise identically (I3).
 */
export function rememberEntity(fog: FogState, key: string, snapshot: FogSnapshot): FogState {
  const merged: Record<string, FogSnapshot> = {};
  const keys = entityKeys(fog.memory).filter((k) => k !== key);
  keys.push(key);
  keys.sort();
  for (const k of keys) merged[k] = k === key ? snapshot : fog.memory[k]!;
  return { seen: fog.seen, memory: merged };
}

export function forgetEntity(fog: FogState, key: string): FogState {
  if (!(key in fog.memory)) return fog;
  const merged: Record<string, FogSnapshot> = {};
  for (const k of entityKeys(fog.memory)) {
    if (k !== key) merged[k] = fog.memory[k]!;
  }
  return { seen: fog.seen, memory: merged };
}

export const recallEntity = (fog: FogState, key: string): FogSnapshot | null =>
  fog.memory[key] ?? null;
